/**
 * Utilidades para normalizar direcciones de colivings (VeCy, mapa).
 * Espejo de backend/src/utils/colivingAddressUtils.ts
 */

function normalizeWhitespace(value) {
  return value.replace(/\s+/g, ' ').trim();
}

export function isGenericPropertyAddress(address, location) {
  const normalizedAddress = normalizeWhitespace(address || '');
  if (!normalizedAddress) return true;

  const normalizedLocation = normalizeWhitespace(location || '').toLowerCase();
  const addressLower = normalizedAddress.toLowerCase();

  if (normalizedLocation) {
    if (addressLower === normalizedLocation) return true;
    if (addressLower === `${normalizedLocation}, ${normalizedLocation}`) return true;
  }

  const parts = normalizedAddress.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
  if (parts.length >= 2 && parts.every((p) => p === parts[0])) return true;

  return false;
}

export function extractHomiiiStreetFromName(name, city = 'Madrid') {
  let raw = normalizeWhitespace(name.replace(/\s*\([^)]+\)\s*$/, ''));

  const cityVariants = [
    city,
    'Madrid',
    'Getafe',
    'Majadahonda',
    'Pozuelo de Alarcón',
    'Pozuelo de Alarcon',
  ];

  for (const cityName of cityVariants) {
    raw = raw.replace(new RegExp(`\\s*,?\\s*${cityName}\\s*$`, 'i'), '').trim();
  }

  raw = raw.replace(/^Edificio en (la )?/i, '');
  raw = raw.replace(/^calle\s+/i, 'Calle ');
  raw = raw.replace(/^paseo\s+/i, 'Paseo ');
  raw = raw.replace(/^avenida\s+/i, 'Avenida ');
  raw = raw.replace(/^ronda\s+/i, 'Ronda ');
  raw = raw.replace(/,\s*,/g, ',');
  raw = raw.replace(/\.\s*º[A-Z]?/gi, '');
  raw = raw.replace(/\s+\b(CI|ID|II|BA|E|B|D)\s*$/i, '');
  raw = raw.replace(/\s+Bajo\s*[A-Z]?$/i, '');
  raw = raw.replace(/\s+\d+\s*º\s*[A-Z]?$/i, '');
  raw = normalizeWhitespace(raw).replace(/,\s*$/, '').trim();

  if (!raw) return '';

  const looksLikeStreet = /^(calle|paseo|avenida|av\.?|ronda|plaza|camino|pasaje|c\/)/i.test(raw);
  if (looksLikeStreet) return raw;

  if (/calle\s/i.test(name)) {
    const match = name.match(/calle\s+([^,]+)/i);
    if (match) return normalizeWhitespace(`Calle ${match[1]}`);
  }

  return raw;
}

export function extractHomiiiStreetFromTitle(title) {
  const match = title.match(/^Homiii\s*\|\s*(.+)$/i);
  if (!match) return '';
  return extractHomiiiStreetFromName(match[1], 'Madrid');
}

export function resolvePropertyAddress(fields) {
  const source = (fields.source || fields.externalSource || '').toLowerCase();
  let stored = normalizeWhitespace(fields.propertyAddress || '');
  if (source === 'spacest' && stored) {
    stored = normalizeSpacestPropertyAddress(stored);
  }

  if (stored && !isGenericPropertyAddress(stored, fields.propertyLocation)) {
    return stored;
  }

  if (fields.title) {
    if (source === 'homiii') {
      const fromTitle = extractHomiiiStreetFromTitle(fields.title);
      if (fromTitle && !isGenericPropertyAddress(fromTitle, fields.propertyLocation)) {
        return fromTitle;
      }
    }

    const homiiiLike = extractHomiiiStreetFromTitle(fields.title);
    if (homiiiLike && !isGenericPropertyAddress(homiiiLike, fields.propertyLocation)) {
      return homiiiLike;
    }
  }

  return stored || null;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripAccents(value) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

const SPACEST_TRAILING_COUNTRY_TOKENS = new Set([
  'spain',
  'spagna',
  'españa',
  'espana',
  'italy',
  'italia',
  'portugal',
  'france',
  'francia',
  'germany',
  'germania',
  'united kingdom',
  'uk',
  'great britain',
  'regno unito',
]);

export function normalizeSpacestPropertyAddress(address) {
  if (!address) return '';
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  while (parts.length > 1) {
    const last = stripAccents(parts[parts.length - 1].toLowerCase());
    if (SPACEST_TRAILING_COUNTRY_TOKENS.has(last)) {
      parts.pop();
    } else {
      break;
    }
  }
  return normalizeWhitespace(parts.join(', '));
}

export function shortenAddressForCardTitle(address, location) {
  let out = normalizeWhitespace(address);
  const loc = normalizeWhitespace(location || '');
  if (loc) {
    out = out.replace(new RegExp(`\\s*,\\s*${escapeRegExp(loc)}\\s*$`, 'i'), '').trim();
  }
  if (out.length > 72) {
    return `${out.slice(0, 69)}...`;
  }
  return out;
}

/** Spacest: título visible = dirección (alineado con VeCy). */
export function resolveSpacestSearchDisplayTitle(fields) {
  const source = (fields.source || fields.externalSource || '').toLowerCase();
  if (source !== 'spacest') return null;

  const address = resolvePropertyAddress(fields);
  if (!address) return null;

  return shortenAddressForCardTitle(address, fields.propertyLocation);
}

export function buildVecyAddressCandidates(fields) {
  const base = resolvePropertyAddress(fields);
  if (!base) return [];

  const location = normalizeWhitespace(fields.propertyLocation || '');
  const candidates = new Set();

  const add = (value) => {
    const normalized = normalizeWhitespace(value);
    if (normalized) candidates.add(normalized);
  };

  add(base);

  if (location && !base.toLowerCase().includes(location.toLowerCase())) {
    add(`${base}, ${location}`);
  }

  const abbrevs = [
    base.replace(/^Calle de la /i, 'C. '),
    base.replace(/^Calle de /i, 'C. '),
    base.replace(/^Calle /i, 'C. '),
    base.replace(/^Avenida de /i, 'Av. '),
    base.replace(/^Avenida /i, 'Av. '),
    base.replace(/^Paseo de /i, 'P.º '),
    base.replace(/Santa Engracia/i, 'Sta Engracia'),
    base.replace(/Sta Engracia/i, 'Santa Engracia'),
  ];

  for (const variant of abbrevs) {
    add(variant);
    if (location && !variant.toLowerCase().includes(location.toLowerCase())) {
      add(`${variant}, ${location}`);
    }
  }

  return [...candidates].slice(0, 5);
}

/**
 * Variantes a partir de una dirección ya construida (p. ej. desde el frontend).
 */
export function buildVecyLookupVariants(address) {
  return buildVecyAddressCandidates({ propertyAddress: address });
}

export function extractStreetMatchToken(address) {
  let street = normalizeWhitespace((address.split(',')[0] || ''));
  street = street.replace(/^Edificio en (la )?/i, '');
  street = street.replace(/^(Calle|C\.|Paseo|P\.º|Avenida|Av\.|Ronda|Plaza|Camino|Pasaje)\s+(de\s+(la\s+)?)?/i, '');
  street = street.replace(/\s+\b(CI|ID|II|BA|E|B|D|Local)\b.*$/i, '');
  street = normalizeWhitespace(street.replace(/\.\s*º.*$/i, ''));

  const token = street
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  if (token.length < 4) return null;
  return token;
}
